import React from 'react';

/**
 * ErrorBoundary global.
 *
 * Sans ce filet, une seule erreur JS non attrapée (échec réseau lu comme
 * `undefined.map`, prop manquante, etc.) corrompt tout l'arbre React :
 * la page devient blanche et cliquer sur le logo ne récupère plus rien —
 * il faut recharger manuellement.
 *
 * Ici on affiche un message convivial + deux boutons :
 * - « Réessayer » remonte le boundary (réinitialise l'état → nouvelle tentative)
 * - « Retour à l'accueil » navigue en dur (contourne le state React corrompu)
 *
 * `resetKey` (typiquement le pathname) déclenche un reset automatique
 * à chaque changement de route, pour que l'utilisateur ne reste pas bloqué
 * en cliquant sur un lien.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Traçable dans la console pour diagnostic user-side, sans crash serveur.
    console.error('[ErrorBoundary]', error, info?.componentStack);
  }

  componentDidUpdate(prevProps) {
    // Reset auto à chaque changement de route — sinon un utilisateur cliquant
    // sur "Accueil" depuis une page cassée verrait toujours l'écran d'erreur.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => this.setState({ hasError: false, error: null });

  handleHome = () => {
    // Navigation dure : contourne toute corruption d'état React.
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-stone-50" data-testid="error-boundary">
        <div className="max-w-lg text-center">
          <p className="text-sm tracking-[0.3em] uppercase text-primary mb-4">Villa Marceau</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-foreground mb-4">
            Un instant… quelque chose s&apos;est mal passé
          </h1>
          <p className="text-muted-foreground mb-8 leading-relaxed">
            La page n&apos;a pas pu se charger correctement. Cela vient sûrement d&apos;une connexion instable
            ou d&apos;un contenu momentanément indisponible.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-foreground text-white font-medium hover:bg-foreground/90 transition-colors"
              data-testid="error-retry-btn"
            >
              Réessayer
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-foreground/30 text-foreground font-medium hover:bg-foreground/5 transition-colors"
              data-testid="error-home-btn"
            >
              Retour à l&apos;accueil
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <pre className="mt-8 text-left text-xs bg-white border rounded p-4 overflow-auto max-h-60 text-red-700">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
